import React, { useState, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import LinkRow from "./LinkRow";

// SERP bucket labels (BE constants) → human-readable headers.
// Order matters: seller-tailored first (most relevant), then prospect, then company.
const BUCKET_DISPLAY = [
  { key: "SELLER_DYNAMIC", label: "Tailored to Your Pitch", icon: "🎯", highlight: true },
  { key: "PROSPECT_THOUGHT_LEADERSHIP", label: "Thought Leadership", icon: "💡" },
  { key: "PROSPECT_NEWS_MENTIONS", label: "News Mentions", icon: "📰" },
  { key: "PROSPECT_LINKEDIN_SIGNAL", label: "LinkedIn Signal", icon: "💼" },
  { key: "PROSPECT_PODCASTS_EVENTS", label: "Podcasts & Events", icon: "🎙️" },
  { key: "PROSPECT_YOUTUBE_TALKS", label: "YouTube & Talks", icon: "📺" },
  { key: "COMPANY_LEADERSHIP_CHANGES", label: "Leadership Changes", icon: "🔁" },
  { key: "COMPANY_JOB_POSTINGS", label: "Hiring Signals", icon: "📋" },
  { key: "COMPANY_FINANCIAL_DATA", label: "Funding & Finance", icon: "💰" },
  { key: "COMPANY_LATKA_DATA", label: "Revenue (Latka)", icon: "📈" },
  { key: "COMPANY_REVIEWS", label: "Reviews (G2/Capterra)", icon: "⭐" },
  { key: "COMPANY_GLASSDOOR_REVIEWS", label: "Glassdoor", icon: "🏢" },
  { key: "COMPANY_COMPETITIVE_SIGNALS", label: "Competitive Signals", icon: "⚔️" },
  { key: "COMPANY_ORG_CHART", label: "Org Structure", icon: "🌳" },
  { key: "COMPANY_CUSTOMERS", label: "Known Customers", icon: "🤝" }
  // NOTE: SELLER_COMPETITOR_LANDSCAPE intentionally hidden. That bucket SERPs
  // articles about the SELLER's competitors (e.g. "Salesgear alternatives"),
  // which is irrelevant noise when the rep is researching a prospect. The
  // same intel is now sourced from seller_intelligence.competitor_map.
  // BE still runs the query for back-compat; safe to remove from
  // SerpService.buildAccountSerpQueries / buildSerpQueries when audited.
];

const PAGE_SIZE = 10;
// Show only the top N links per bucket by default — rep scans fast, expands
// when needed. Was 10; collapsing to 3 makes the panel ~70% shorter and
// surfaces the most-relevant link first.
const DEFAULT_VISIBLE = 3;
const SKELETON_ROWS = 3;
// ~5 links/sec reveal pacing inside a bucket. Matches the legacy /userSearch
// "links flooding in" feel — fast enough to feel productive, slow enough that
// the rep registers each link landing rather than seeing one big batch.
const REVEAL_INTERVAL_MS = 180;

// ── Bucket skeleton: shimmer rows shown while a bucket hasn't streamed in yet.
// Gives the panel a "we know we're going to find data in all these buckets,
// just waiting for it" feel during the ~3–8s SERP fan-out.
const SkeletonRows = ({ count = SKELETON_ROWS }) => (
  <ul className="drSrcList drSrcSkeletonList">
    {Array.from({ length: count }).map((_, i) => (
      <li key={i} className="drSrcSkeletonItem">
        <span className="drSrcSkeletonFavicon" />
        <span className="drSrcSkeletonTitle" />
      </li>
    ))}
  </ul>
);

const SourceBucket = React.forwardRef(({ bucket, label, icon, items, status, crawlStatus, highlight, headerExtra, animateIn }, ref) => {
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE);
  // Reveal counter for the "links flooding in" animation. Increments every
  // REVEAL_INTERVAL_MS while < items.length. Only enabled when animateIn is
  // true (initial live arrival) — for cached/persisted research we render
  // everything immediately so the rep doesn't sit through animation on reload.
  const [revealed, setRevealed] = useState(0);
  const firstRenderItemsLen = useRef(items ? items.length : 0);

  useEffect(() => {
    if (!items) return undefined;
    if (!animateIn) {
      // Non-live mode: reveal everything instantly.
      setRevealed(items.length);
      return undefined;
    }
    if (revealed >= items.length) return undefined;
    const t = setTimeout(() => {
      setRevealed((r) => Math.min(r + 1, items.length));
    }, REVEAL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [revealed, items?.length, animateIn]);

  if (status === "pending") {
    return (
      <div ref={ref} className={`drSrcBucket drSrcBucket--pending ${highlight ? "drSrcBucket--highlight" : ""}`}>
        <div className="drSrcBucketHeader">
          <span className="drSrcBucketIcon">{icon}</span>
          <h4 className="drSrcBucketTitle">{label}</h4>
          <span className="drSrcBucketCount drSrcBucketCount--pending">…</span>
        </div>
        <SkeletonRows />
      </div>
    );
  }

  if (!items || items.length === 0) return null;

  // Visible = min(pagination cap, revealed-by-animation). On non-live load
  // revealed snaps to items.length on first render so we skip the animation.
  const effectiveCount = Math.min(visibleCount, revealed);
  const visible = items.slice(0, effectiveCount);
  const remaining = items.length - visibleCount;
  const nextChunk = Math.min(PAGE_SIZE, remaining);
  // Show the "load more" button only after we've revealed up to the current
  // pagination cap — otherwise it appears before the rep has even seen the
  // first 10 land.
  const showLoadMore = remaining > 0 && revealed >= visibleCount;

  return (
    <div ref={ref} className={`drSrcBucket ${highlight ? "drSrcBucket--highlight" : ""}`}>
      <div className="drSrcBucketHeader">
        <span className="drSrcBucketIcon">{icon}</span>
        <h4 className="drSrcBucketTitle">{label}</h4>
        <span className="drSrcBucketCount">{items.length}</span>
      </div>
      {headerExtra}
      <ul className="drSrcList">
        {visible.map((item, i) => (
          <li key={`${bucket}-${i}`} className="drSrcListItem">
            <LinkRow
              link={item}
              status={crawlStatus[item.link]}
              showSnippet
              showMeta
              animationDelay={0}
            />
          </li>
        ))}
      </ul>
      {showLoadMore && (
        <button
          type="button"
          className="drSrcSeeMore"
          onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
        >
          Show {nextChunk} more <span className="drSrcSeeMoreCount">({remaining} hidden)</span>
        </button>
      )}
      {visibleCount > DEFAULT_VISIBLE && (
        <button
          type="button"
          className="drSrcCollapse"
          onClick={() => setVisibleCount(DEFAULT_VISIBLE)}
        >
          Collapse
        </button>
      )}
    </div>
  );
});
SourceBucket.displayName = "SourceBucket";

const DynamicQueriesPreview = ({ queries, reasoning }) => {
  if (!queries || queries.length === 0) return null;
  return (
    <div className="drSrcDynPreview">
      {reasoning && <p className="drSrcDynReasoning">{reasoning}</p>}
      <ul className="drSrcDynQueries">
        {queries.map((q, i) => (
          <li key={i} className="drSrcDynQuery"><span>🔍</span> <code>{q}</code></li>
        ))}
      </ul>
    </div>
  );
};

const ResearchSourcesPanel = observer(({
  serpLinks,
  crawlStatus,
  dynamicQueries,
  dynamicReasoning,
  isLive
}) => {
  // Per-bucket DOM refs so we can scroll-into-view the bucket that's currently
  // streaming in. Keyed by bucket label.
  const bucketRefs = useRef({});
  // Tracks which buckets we've already auto-scrolled to so we don't keep
  // jumping back when items stream into an already-seen bucket.
  const seenArrivals = useRef(new Set());
  // Suppress auto-scroll once the user has manually scrolled — don't fight them.
  const userScrolledAway = useRef(false);
  // Panel open/closed state. Stays open while research is streaming (isLive).
  // Once research completes, auto-collapses so the synthesis sections (selling
  // signals, starters, etc.) are the focus — sources are still one click away
  // for the rep who wants to drill in.
  const [open, setOpen] = useState(true);
  // Track when isLive flips false → auto-collapse exactly once. Subsequent
  // user toggles are respected (don't keep re-collapsing on every re-render).
  const autoCollapsedRef = useRef(false);
  useEffect(() => {
    if (!isLive && !autoCollapsedRef.current) {
      autoCollapsedRef.current = true;
      setOpen(false);
    }
  }, [isLive]);

  // Compute counts + arrival status per bucket. `serpLinks` is keyed by label.
  // A bucket is "loaded" if it's present with an array; "pending" if not yet
  // and we're still live (research-in-progress); skipped if not present and
  // research is done (bucket genuinely had no results).
  const stats = useMemo(() => {
    let total = 0;
    let loadedBuckets = 0;
    for (const { key } of BUCKET_DISPLAY) {
      const b = serpLinks?.[key];
      if (Array.isArray(b)) {
        total += b.length;
        if (b.length > 0) loadedBuckets += 1;
      }
    }
    return { total, loadedBuckets };
  }, [serpLinks]);

  // Track user scrolls — once they scroll up manually, stop auto-following.
  // Reset the flag when isLive transitions back to true (new research run).
  useEffect(() => {
    if (!isLive) return undefined;
    userScrolledAway.current = false;
    seenArrivals.current = new Set();
    const onWheel = () => { userScrolledAway.current = true; };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchmove", onWheel, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchmove", onWheel);
    };
  }, [isLive]);

  // Auto-scroll to the latest-arrived bucket while live. Runs whenever
  // serpLinks changes; skips buckets we've already scrolled to.
  useEffect(() => {
    if (!isLive) return;
    if (userScrolledAway.current) return;
    // Find the latest bucket that has data and we haven't seen yet, in display order
    for (const { key } of BUCKET_DISPLAY) {
      const b = serpLinks?.[key];
      if (Array.isArray(b) && b.length > 0 && !seenArrivals.current.has(key)) {
        seenArrivals.current.add(key);
        const node = bucketRefs.current[key];
        if (node && typeof node.scrollIntoView === "function") {
          // Smooth + nearest = follow the stream without yanking the page hard
          node.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    }
  }, [serpLinks, isLive]);

  // While live, render even with zero buckets so the skeletons can show.
  // When not live, only render if we have actual data.
  if (!isLive && stats.total === 0) return null;

  return (
    <div className={`drSourcesPanel ${open ? "" : "drSourcesPanel--collapsed"}`}>
      <button
        type="button"
        className="drSourcesHeader drSourcesHeader--toggle"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <i className="material-icons-outlined drSourcesChevron">
          {open ? "expand_more" : "chevron_right"}
        </i>
        <h3 className="drSourcesTitle">📚 Research Sources</h3>
        <span className={`drSourcesCount ${isLive ? "drSourcesCount--live" : ""}`}>
          {isLive ? (
            <>
              <span className="drSourcesLiveDot" aria-hidden="true" />
              Found <b>{stats.total}</b> across <b>{stats.loadedBuckets}</b> categories so far…
            </>
          ) : (
            <>
              <b>{stats.total}</b> links across <b>{stats.loadedBuckets}</b> categories
            </>
          )}
        </span>
      </button>
      {open && (
      <div className="drSourcesBuckets">
        {BUCKET_DISPLAY.map(({ key, label, icon, highlight }) => {
          const bucket = serpLinks?.[key];
          const arrived = Array.isArray(bucket);

          if (!arrived) {
            // Live + no data yet → skeleton placeholder so the rep sees
            // *all* the buckets we plan to populate. After research ends,
            // hide unfilled buckets to keep the panel tidy.
            if (!isLive) return null;
            return (
              <SourceBucket
                key={key}
                ref={(el) => { bucketRefs.current[key] = el; }}
                bucket={key}
                label={label}
                icon={icon}
                highlight={highlight}
                status="pending"
                crawlStatus={crawlStatus || {}}
              />
            );
          }

          return (
            <SourceBucket
              key={key}
              ref={(el) => { bucketRefs.current[key] = el; }}
              bucket={key}
              label={label}
              icon={icon}
              highlight={highlight}
              items={bucket}
              crawlStatus={crawlStatus || {}}
              animateIn={isLive}
              headerExtra={
                key === "SELLER_DYNAMIC"
                  ? <DynamicQueriesPreview queries={dynamicQueries} reasoning={dynamicReasoning} />
                  : null
              }
            />
          );
        })}
      </div>
      )}
    </div>
  );
});

export default ResearchSourcesPanel;
